
import { Component, Vue, Watch } from "vue-property-decorator";
import AppSidebarHead from "./AppSidebarHead.vue";
import AppSidebarLink from "./AppSidebarLink.vue";
import { TSidebarLink } from "@/models/sidebar";
import {
  employerLinks,
  employerLinksMember,
  employerLinksNoSponsor,
} from "@/pages/Layout/views/Employer/REmployer";
import { employeeLinks } from "@/pages/Layout/views/Employee/REmployee";
import { candidateLinks } from "@/pages/Layout/views/Candidate/RCandidate";
import { expertLinks } from "@/pages/Layout/views/Expert/RExpert";
import { SMAdditionalInfo, SMUser } from "@/store";
import { track } from "@amplitude/analytics-browser";

import { MEMBER_VALUE } from "@/constants/teammates";
import AppSidebarUpgradeBadge from "@/components/V2/AppSidebar/AppSidebarUpgradeBadge.vue";
import { chatwootShutdown } from "@/utils/chatwoot";

const LOCAL_STORAGE_KEY_CASE_ID = "case_id";
const LOGIN_PATH = "/auth/login";

const ICONS = {
  logOut: "log-out-outline",
  teammates: "person-add-outline",
  chevronFroward: "chevron-forward-outline",
  quickActions: "layout-grid-add",
};

const WIDTH_CW = 480 + 254;

const MY_ACCOUNT_ROUTE = "MyAccount";
const TEAMMATES_ROUTE = "Teammates";

const CLASS = "sidebar";

@Component({
  components: {
    AppSidebarUpgradeBadge,
    AppSidebarHead,
    AppSidebarLink,
  },
})
export default class AppSidebarNew extends Vue {
  @Watch("user.if_sponsor")
  ifSponsorChanged(val: boolean) {
    if (!val) return;

    this.filteredSidebarLinks = this.navLinks.filter((link) => !link.isChild);
  }

  icons = ICONS;

  get showPremiumBadge() {
    if (!SMUser.isEmployer) return false;

    return SMUser.isSponsor && !SMUser.isPremiumSubscriptionActive;
  }

  quickActionsDropdownVisible = false;

  // isProduction = process.env.NODE_ENV === "production";
  isProduction = false;

  get isLocalStorageCollapsed() {
    return localStorage.getItem("is_sidebar_collapsed") === "true";
  }

  isCollapsed = this.isLocalStorageCollapsed;

  get contextMenuProps() {
    return {
      theme: "context-menu",
      triggers: ["click"],
      popperTriggers: ["click"],
      placement: "right-start",
      distance: 16,
      interactive: true,
      hideOnClick: true,
    };
  }

  // Quick actions
  handleOutsideQuickActions() {
    const dropdown = this.$refs.quickActionsDropdown as any;

    if (dropdown?.hide) {
      dropdown.hide();
    }
  }

  // get quickActions() {
  //   return SMAdditionalInfo?.filteredQuickActions
  //     ? [...SMAdditionalInfo?.filteredQuickActions]
  //     : [];
  // }

  // get preparedQuickActions() {
  //   return this.quickActions.filter((item) => item.disabled === false);
  // }

  get preparedQuickActions() {
    if (SMUser.isEmployer) return SMAdditionalInfo.filteredQuickActions;

    if (SMUser.isEmployee) return SMAdditionalInfo.filteredQuickActionsEmployee;

    return [];
  }

  get user() {
    return SMUser.user;
  }

  get isCompliance() {
    return SMUser.isEmployer && SMUser.isSponsor;
  }

  get showQuickActions() {
    return SMUser.isEmployer || SMUser.isEmployee;
  }

  get quickActionsLink() {
    return { label: "Quick Actions", icon: ICONS.quickActions };
  }

  get links() {
    return this.filteredSidebarLinks.length ? this.filteredSidebarLinks : this.filteredLinks;
  }

  get filteredLinks() {
    return this.navLinks.filter((link) => {
      return !link.isChild;
    });
  }

  filteredSidebarLinks: TSidebarLink[] = this.filteredLinks;

  get classes() {
    return {
      [`${CLASS}_collapsed`]: this.isCollapsed,
    };
  }

  get routerLinks() {
    if (SMUser.isEmployer && SMUser.user?.permission == MEMBER_VALUE) return employerLinksMember;

    if (SMUser.isEmployer && !SMUser.user?.sponsor_confirmation) return employerLinksNoSponsor;

    if (SMUser.isEmployer) return employerLinks;

    if (SMUser.isEmployee) return employeeLinks;

    if (SMUser.isCandidate) return candidateLinks;

    if (SMUser.isExpert) return expertLinks;

    return [];
  }

  get showTeammates() {
    if (!SMUser.isEmployer) return false;

    if (SMUser.user?.permission == MEMBER_VALUE) return false;

    return true;
  }

  get routes() {
    return (this.routerLinks as any).reduce((acc, cur, index, array) => {
      if (!Object.prototype.hasOwnProperty.call(cur, "children")) {
        Object.assign(cur, { children: [] });
      }

      if (cur.childLabels.length) {
        cur.childLabels.forEach((childLabel) => {
          const child = array.find((item) => item.label === childLabel);

          const exist = cur.children.findIndex((item) => item.label === child.label) > -1;

          if (child && !exist) cur.children.push(child);
        });
      }

      Object.assign(cur, {
        icon: cur.newIcon ?? cur.icon ?? "",
      });

      if (cur.path) {
        const method = () => {
          this.$router.push(cur.path);
        };

        Object.assign(cur, { method });
      }

      if (!cur.isChild && !cur.hide) {
        acc.push(cur);
      }

      return acc;
    }, []);
  }

  getRouteChildren(children) {
    return children.map((child) => {
      const { label, icon, newIcon, path } = child;

      const item = {
        label,
        icon: newIcon ?? icon ?? "",
        // icon: null,
      };

      if (path) {
        const method = () => {
          this.$router.push(path);
        };

        Object.assign(item, { method });
      }
      return item;
    });
  }

  get navLinks(): TSidebarLink[] {
    return this.routerLinks
      .map(({ label, newIcon, icon, path, childLabel, childLabels, isChild, hide, basePath }) => ({
        label,
        icon: newIcon ?? icon ?? "",
        path,
        childLabel,
        childLabels,
        isChild,
        hide,
        basePath,
        isOpen: false,
      }))
      .filter(({ hide }) => {
        return !hide;
      });
  }

  hasChild(childLabel: string) {
    return !!this.navLinks.find((link) => link.label === childLabel);
  }

  get accountLink(): TSidebarLink {
    return {
      icon: this.icons.logOut,
      avatar: true,
      label: SMUser?.user?.first_name ?? this.$tc("labels.myAccount"),
      action: async () => await this.goAccount(),
    };
  }

  async goAccount() {
    try {
      await this.$router.push({
        name: MY_ACCOUNT_ROUTE,
      });
    } catch {
      return;
    }
  }

  get teammatesLink(): TSidebarLink {
    return {
      icon: this.icons.teammates,
      label: this.$tc("labels.addTeammates"),
      action: async () => await this.goTeammates(),
    };
  }

  async goTeammates() {
    try {
      await this.$router.push({
        name: TEAMMATES_ROUTE,
      });
    } catch {
      return;
    }
  }

  get logoutLink(): TSidebarLink {
    return {
      icon: this.icons.logOut,
      label: this.$tc("labels.logOut"),
      action: () => this.logout(),
    };
  }

  shutdownIntercom() {
    if (!this.isProduction) return;
    chatwootShutdown();
  }

  logout() {
    this.shutdownIntercom();

    localStorage.removeItem(LOCAL_STORAGE_KEY_CASE_ID);

    this.$auth.logout({ redirect: LOGIN_PATH });
  }

  toggleCollapsed() {
    this.isCollapsed = !this.isCollapsed;

    localStorage.setItem("is_sidebar_collapsed", this.isCollapsed ? "true" : "false");
  }

  sidebarLinkToggle(payload: any) {
    this.filteredSidebarLinks = this.navLinks
      .filter((link) => {
        const activeLinks =
          (payload.isOpen &&
            link.isChild &&
            (link.label === payload.childLabel || payload.childLabels?.includes(link.label))) ||
          this.filteredLinks.includes(link);

        return activeLinks;
      })
      .map((link) => {
        link.isOpen = false;

        if (link.label === payload.label) link.isOpen = payload.isOpen;

        return link;
      });
  }

  checkCW() {
    const isCWWideEnough = document.documentElement.clientWidth >= WIDTH_CW;

    if (isCWWideEnough) return;

    this.isCollapsed = true;
  }

  addResizeListener() {
    this.checkCW();

    window.addEventListener("resize", () => this.checkCW());
  }

  toTimestamp(date: string | undefined) {
    if (!date) return;

    return Math.round(new Date(date).getTime() / 1000);
  }

  logEvent(link: string) {
    track("Sidebar Link Clicked", { link });
  }

  mounted() {
    if (!SMUser.user) SMUser.updateCurrentUser();

    this.addResizeListener();
  }

  get showRecruitmenButton(): boolean {
    if (!SMUser.isEmployer) return false;

    if (this.isCollapsed) return false;

    return SMUser.isSponsorConfirmation;
  }

  get routePath() {
    return this.$route.path;
  }

  @Watch("routePath")
  onRoutePathChanged(path: string) {
    const found = [...this.filteredSidebarLinks, ...this.filteredLinks].some(
      (link) => link.path === path
    );

    if (!found) {
      this.sidebarLinkToggle({ isOpen: false, childLabel: "", label: "" });
    }
  }
}
